import { Divider, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { DefaultSchoolYear, FilterKeys, GetGroupsQueryFilters, GroupArrangeBy } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  ArrangeBySelect,
  FiltersSidebar,
  FiltersSidebarMethods,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  StudentStaffPropertyTypeSelectMultiple,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
  UserSelectMultiple,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { FC, forwardRef, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGroupsFilters } from './useGroupsFilters';

const noop = () => {};

type GroupsFiltersSidebarProps = {
  defaultSchoolYear?: DefaultSchoolYear;
  filters: GetGroupsQueryFilters;
  arrangeBy: GroupArrangeBy | null;
  defaultFilters: GetGroupsQueryFilters;
  schoolId: string;
  onApply: (v: { filters: GetGroupsQueryFilters; arrangeBy: GroupArrangeBy | null }) => void;
};

export const GroupsFiltersSidebar = forwardRef<FiltersSidebarMethods, GroupsFiltersSidebarProps>(
  (
    {
      filters: propsFilters,
      defaultFilters,
      onApply,
      schoolId,
      arrangeBy: propsArrangeBy,
      defaultSchoolYear,
    },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [arrangeBy, setArrangeBy] = useState(propsArrangeBy);
    const { permissions, currentStaff } = useAuth();
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
    const {
      labels,
      hasHouses,
      hasSubjects,
      filtersDate,
      filtersAgeGroup,
      filtersSubject,
      filtersStaff,
      filtersStudentStatus,
      filtersStaffStatus,
      filtersStudentHouse,
      filtersStaffHouse,
      filtersOnlyTutorGroups,
      onSetDate,
      onSetAgeGroup,
      onClearAgeGroup,
      onSetSubjectId,
      onClearSubject,
      onSetStaffId,
      onClearStaff,
      onSetStudentStaffStatusIds,
      onClearStatuses,
      onSetStudentStaffHouseIds,
      onClearHouses,
      onSetOnlyTutorGroups,
      arrangeByDropdownOptions,
    } = useGroupsFilters({ schoolId, filters, onSetFilters: setFilters });

    const { formatMessage } = useIntl();
    const isOnlyTutorGroups = !!filtersOnlyTutorGroups?.[0];
    const handleApply = useCallback(() => {
      onApply({ filters, arrangeBy });
    }, [filters, onApply, arrangeBy]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      setArrangeBy(null);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    const onOpen = useCallback(() => {
      setFilters(propsFilters);
      setArrangeBy(propsArrangeBy);
    }, [propsFilters, propsArrangeBy]);

    const renderSubjectSelect = () => {
      if (!hasSubjects) return null;

      if (isOnlyTutorGroups) {
        return (
          <Tooltip
            title={formatMessage({ id: 'groups-GroupSubject-disabledFilter-Hint' })}
            placement="bottom-end"
          >
            <div>
              <SubjectSelectMultiple
                disabled
                placeholder={labels[FilterKeys.Subject]}
                schoolId={schoolId}
                onSelectSubjectId={noop}
                selectedIds={[]}
                testId={DROPDOWN.SUBJECT}
              />
            </div>
          </Tooltip>
        );
      }

      return (
        <SubjectSelectMultiple
          placeholder={labels[FilterKeys.Subject]}
          schoolId={schoolId}
          onSelectSubjectId={(v: string) =>
            onSetSubjectId(toggleMultipleValueArrayProperty(filtersSubject, [v]))
          }
          selectedIds={filtersSubject || []}
          onClear={onClearSubject}
          onOpen={preventClose}
          onClose={allowClose}
          testId={DROPDOWN.SUBJECT}
        />
      );
    };

    return (
      <FiltersSidebar
        onOpen={onOpen}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Groups' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <FilterSection title={formatMessage({ id: 'groups-FilterSection-ByGroupParameters' })}>
          <SchoolPeriodsRangeSelect
            ref={periodsRef}
            showSelectedPeriodLabel={false}
            schoolId={schoolId}
            defaultSchoolYear={defaultSchoolYear}
            hasManagePermission={permissions.includes('school_admin')}
            onSetDate={onSetDate}
            placeholder={labels[FilterKeys.Date]}
            date={filtersDate || []}
            requiredLabel="required"
            onOpen={preventClose}
            onClose={allowClose}
          />
          <AgeGroupSelectMultiple
            allowArchived
            userRole={SchoolUserRole.Student}
            placeholder={labels[FilterKeys.AgeGroup]}
            schoolId={schoolId}
            onSelect={(v) => onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, v))}
            selectedIds={filtersAgeGroup || []}
            onClear={onClearAgeGroup}
            onOpen={preventClose}
            onClose={allowClose}
            testId={DROPDOWN.AGE_GROUP}
          />
          <UserSelectMultiple
            type="staff"
            placeholder={labels[FilterKeys.Staff]}
            schoolId={schoolId}
            currentUser={currentStaff}
            onSelectUserId={(v: string) =>
              onSetStaffId(toggleMultipleValueArrayProperty(filtersStaff, [v]))
            }
            selectedIds={filtersStaff || []}
            onClear={onClearStaff}
            filters={filters}
            onOpen={preventClose}
            onClose={allowClose}
            testId={DROPDOWN.STAFF}
          />
          {renderSubjectSelect()}
          <FormControlLabel
            sx={{
              px: 1.5,
            }}
            control={
              <Switch
                checked={isOnlyTutorGroups}
                // biome-ignore lint/complexity/noUselessTernary: Legacy code
                onChange={() => onSetOnlyTutorGroups(isOnlyTutorGroups ? false : true)}
              />
            }
            label={formatMessage({ id: 'groups-OnlyTutorGroups' })}
          />
        </FilterSection>

        <FilterSection title={formatMessage({ id: 'groups-FilterSection-ByMemberParameters' })}>
          <StudentStaffPropertyTypeSelectMultiple
            placeholder={labels.status}
            schoolId={schoolId}
            onSelectStaffId={(v: string) =>
              onSetStudentStaffStatusIds({
                staffIds: toggleMultipleValueArrayProperty(filtersStaffStatus, [v]),
              })
            }
            onSelectStudentId={(v: string) =>
              onSetStudentStaffStatusIds({
                studentIds: toggleMultipleValueArrayProperty(filtersStudentStatus, [v]),
              })
            }
            selectedStaffIds={filtersStaffStatus || []}
            selectedStudentIds={filtersStudentStatus || []}
            onClear={onClearStatuses}
            propertyType={SchoolPropertyType.Status}
            onOpen={preventClose}
            onClose={allowClose}
            testId={DROPDOWN.STATUS}
          />
          {hasHouses && (
            <StudentStaffPropertyTypeSelectMultiple
              placeholder={labels.house}
              schoolId={schoolId}
              onSelectStudentId={(v: string) =>
                onSetStudentStaffHouseIds({
                  studentIds: toggleMultipleValueArrayProperty(filtersStudentHouse, [v]),
                })
              }
              onSelectStaffId={(v: string) =>
                onSetStudentStaffHouseIds({
                  staffIds: toggleMultipleValueArrayProperty(filtersStaffHouse, [v]),
                })
              }
              selectedStaffIds={filtersStaffHouse || []}
              selectedStudentIds={filtersStudentHouse || []}
              onClear={onClearHouses}
              propertyType={SchoolPropertyType.House}
              onOpen={preventClose}
              onClose={allowClose}
            />
          )}
        </FilterSection>

        <Divider />

        <ArrangeBySelect
          options={arrangeByDropdownOptions}
          selectedOption={arrangeBy ?? undefined}
          placeholder={labels.arrangeBy}
          onClear={() => setArrangeBy(null)}
          onSelectOption={setArrangeBy}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);

const FilterSection: FC<PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <Stack data-scope>
    <Typography variant="h4" color="text.secondary" mb={2}>
      {title}
    </Typography>
    <Stack gap={2}>{children}</Stack>
  </Stack>
);
